// components/ShopDetailModal.jsx
import React, { useEffect } from 'react';
import { NIGHT_EYE_IMG_URL } from '../../Process';

function ShopDetailModal({ shop, isOpen, onClose }) {
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    if (!isOpen || !shop) return null;

    // Extract all top-level fields (excluding array fields for separate handling)
    const {
        ShopName = '-',
        ShopImage,
        ShopMobileNo1,
        ShopMobileNo2,
        ShopMobileNo3,
        ShopOpenTime,
        ShopCloseTime,
        Address,
        CityName,
        ZoneName,
        PoliceStation,
        AreaName,
        OtherArea,
        Type,
        OtherTypeMasterName,
        ShopUniqueID,
        Latitude,
        Longitude,
        ShopVerifiedStatus,
        ShopVerifiedType,
        ShopVerifiedByUserID,
        ShopVerifiedByDateTime,
        IsShopOwnorOnRentOrOnSubTenant,
        IsShopOpenByOtherEmployee,
        RentExpiryDate,
        CreatedDate,
        IsActive,
        IsDelete,
        RowNum,
        ShopUserDetail = [],
        ShopOwnerDetail = [],
        ShopHelperDetail = [],
        ShopAmenitiesDetail = [],
        ShopAgreementDetail = []
    } = shop;

    // We only show the first owner as requested earlier
    const singleOwner = ShopOwnerDetail?.[0] || null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="absolute inset-0" onClick={onClose} />

            <div className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white border-b px-6 py-4 flex items-center justify-between">
                    <div className='flex '> <img
                        src={`${NIGHT_EYE_IMG_URL}/SmartCollection/ShopImage/${ShopImage}`}
                        alt="Agreement Image"
                        className="object-cover"
                        width={"100px"}

                    />
                        <h2 className="m-4 text-2xl font-bold text-gray-900 truncate pr-10">
                            {ShopName || 'Shop Details'}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-gray-100"
                    >
                        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-6 md:p-8 space-y-8">
                    {/* 1. Basic shop info */}
                    <section>
                        <h3 className="text-xl font-semibold mb-4">Basic Information</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                            <div>
                                <p className="text-gray-500">Shop Name</p>
                                <p className="font-medium">{ShopName || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Type</p>
                                <p className="font-medium">{Type || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Other Type</p>
                                <p className="font-medium">{OtherTypeMasterName || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Mobile No 1</p>
                                <p className="font-medium">{ShopMobileNo1 || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Mobile No 2</p>
                                <p className="font-medium">{ShopMobileNo2 || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Mobile No 3</p>
                                <p className="font-medium">{ShopMobileNo3 || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Open Time</p>
                                <p className="font-medium">{ShopOpenTime || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Close Time</p>
                                <p className="font-medium">{ShopCloseTime || '-'}</p>
                            </div>
                            <div className="col-span-2 lg:col-span-3">
                                <p className="text-gray-500">Address</p>
                                <p className="whitespace-pre-line">{Address || '-'}</p>
                            </div>
                        </div>
                    </section>

                    {/* 2. Location & Verification */}
                    <section>
                        {/* <h3 className="text-xl font-semibold mb-4">Location & Verification</h3> */}
                        <h3 className="text-xl font-semibold mb-4">Location</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                            <div>
                                <p className="text-gray-500">City</p>
                                <p className="font-medium">{CityName || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Zone</p>
                                <p className="font-medium">{ZoneName || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Police Station</p>
                                <p className="font-medium">{PoliceStation || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Area</p>
                                <p className="font-medium">{AreaName || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Other Area</p>
                                <p className="font-medium">{OtherArea || '-'}</p>
                            </div>
                            {/* <div>
                                <p className="text-gray-500">Latitude</p>
                                <p className="font-medium">{Latitude || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Longitude</p>
                                <p className="font-medium">{Longitude || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Verified Status</p>
                                <p className="font-medium">{ShopVerifiedStatus || '-'}</p>
                            </div> 
                            <div>
                                <p className="text-gray-500">Verified Type</p>
                                <p className="font-medium">{ShopVerifiedType || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Verified By User ID</p>
                                <p className="font-medium">{ShopVerifiedByUserID || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Verified DateTime</p>
                                <p className="font-medium">{ShopVerifiedByDateTime || '-'}</p>
                            </div>*/}
                        </div>
                    </section>

                    {/* 3. Ownership & Other Status */}
                    <section>
                        <h3 className="text-xl font-semibold mb-4">Ownership & Status</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                            <div>
                                <p className="text-gray-500">Ownership Type</p>
                                <p className="font-medium">{IsShopOwnorOnRentOrOnSubTenant || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Open by Other Employee</p>
                                <p className="font-medium">{IsShopOpenByOtherEmployee ? 'Yes' : 'No'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Rent Expiry Date</p>
                                <p className="font-medium">{RentExpiryDate || '-'}</p>
                            </div>
                            <div>
                                {/* <p className="text-gray-500">Created Date</p> */}
                                <p className="text-gray-500">On Portal Registered Date</p>
                                <p className="font-medium">{CreatedDate || '-'}</p>
                            </div>
                            {/* <div>
                                <p className="text-gray-500">Is Active</p>
                                <p className="font-medium">{IsActive ? 'Yes' : 'No'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Is Deleted</p>
                                <p className="font-medium">{IsDelete ? 'Yes' : 'No'}</p>
                            </div> */}

                        </div>
                    </section>

                    <section>
                        <h3 className="text-xl font-semibold mb-4">Shop User Detail</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                            <div>
                                <p className="text-gray-500">Name</p>
                                <p className="font-medium">{ShopUserDetail?.[0]?.ShopUserName || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Mobile</p>
                                <p className="font-medium">{ShopUserDetail?.[0]?.ShopUserMobileNo || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Aadhaar No</p>
                                <p className="font-medium">{ShopUserDetail?.[0]?.AadhaarCardNo || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Aadhaar Image:</p>
                                <p className="font-medium">{ShopUserDetail?.[0]?.AadhaarCardImage ?
                                    <img
                                        src={`${NIGHT_EYE_IMG_URL}/SmartCollection/AadhaarCardImage/${ShopUserDetail?.[0]?.AadhaarCardImage}`}
                                        alt="Adhar Card Image"
                                        className=" object-contain "
                                        width={"300px"}
                                    />
                                    : '-'}</p>
                            </div>

                        </div>
                    </section>

                    {singleOwner && <section>
                        <h3 className="text-xl font-semibold mb-4">Shop Owner Detail</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                            <div>
                                <p className="text-gray-500">Name</p>
                                <p className="font-medium">{singleOwner?.ShopUserName || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Mobile</p>
                                <p className="font-medium">{singleOwner?.OwnerMobileNo || '-'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Address</p>
                                <p className="font-medium">{singleOwner?.OwnerAddress || '-'}</p>
                            </div>
                        </div>
                    </section>}

                    {ShopHelperDetail?.length > 0 && (
                        <section>
                            <h3 className="text-xl font-semibold mb-4">Shop Helpers</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {ShopHelperDetail?.map((helper, index) => (
                                    <div key={index} className="p-4 bg-gray-50 rounded-lg">
                                        <p><strong>Name:</strong> {helper.HelperName || '-'}</p>
                                        <p><strong>Mobile:</strong> {helper.HelperMobileNo || '-'}</p>
                                        <p><strong>Role:</strong> {helper.HelperRoleName || '-'}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {ShopAmenitiesDetail?.length > 0 && (
                        <section>
                            <h3 className="text-xl font-semibold mb-4">Amenities</h3>
                            <ul className="space-y-2">
                                {ShopAmenitiesDetail?.map((item, index) => (
                                    <li key={index} className="flex justify-between p-3 bg-gray-50 rounded-lg">
                                        <span>{item.AmenitiesName || '-'}</span>
                                        <span className={item.IsAvailable ? 'text-green-600' : 'text-red-600'}>
                                            {item.IsAvailable ? 'Available' : 'Not Available'}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {ShopAgreementDetail?.length > 0 && (
                        <section>
                            <h3 className="text-xl font-semibold mb-4">Agreement Documents</h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                {ShopAgreementDetail?.map((doc, index) => (
                                    <div key={index} className="aspect-square bg-gray-100 rounded-lg overflow-hidden">

                                        <img
                                            src={`${NIGHT_EYE_IMG_URL}/SmartCollection/ShopAgreementImage/${doc?.Attachment}`}
                                            alt="Agreement Image"
                                            className="w-full h-full object-cover"
                                            width={"300px"}
                                        />
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ShopDetailModal;